/**
 * Route-level guards for the granular CRM permission model.
 *
 * Two pieces:
 *
 *   - `<CrmLandingRedirect>` — replaces the old hard `/crm → /crm/tasks`
 *     redirect. The CRM's primary page is now AI Prospects, so `/crm` prefers
 *     `/crm/ai-prospects`. But with granular page permissions a role may not
 *     have prospects access at all (and a marketing manager who does lands on
 *     it fine), so a hard redirect would dead-end on Access-Denied. This sends
 *     each user to the FIRST CRM page they can actually open, in preference
 *     order (AI Prospects → Prospects → Tasks → …).
 *
 *   - `<RequireFinancialOutlet>` — the route-level half of the financial wall.
 *     The Invoices, Quotes and Financial-Metrics pages were previously only
 *     hidden from the nav; a direct URL still rendered them (and their dollar
 *     figures). Wrapping those routes in this guard turns a hidden page into a
 *     genuinely blocked one: no `financials:access` ⇒ Access Denied, not a
 *     leaked invoice total.
 */
import { Navigate, Outlet } from "react-router-dom";

import usePermissions from "@crm-src/hooks/usePermissions";

import { PagePermissionGuard } from "./page-permission-guard.js";

/** CRM pages in landing-preference order: the first one a user can access is
 * where `/crm` sends them. AI Prospects is the primary page, so it leads. It
 * shares the `prospects` page grant with regular Prospects, so a user WITHOUT
 * prospects access falls straight through both prospect entries to the first
 * other page they can open (Tasks, then Customers, …) — that is the "no AI
 * Prospects access ⇒ a different primary page" rule. Non-financial people
 * surfaces come before any guarded money page so a finance-blocked role lands
 * somewhere useful. `page` is the `canAccessPage` key; `path` is the route. */
const LANDING_ORDER: ReadonlyArray<{ page: string; path: string }> = [
  { page: "prospects", path: "ai-prospects" },
  { page: "prospects", path: "prospects" },
  { page: "tasks", path: "tasks" },
  { page: "customers", path: "customers" },
  { page: "contacts", path: "contacts" },
  { page: "file_manager", path: "file-manager" },
  { page: "resellers", path: "administration/resellers" },
  { page: "invoices", path: "invoices" },
  { page: "licences", path: "licences" },
  { page: "products_pricing", path: "releases" },
  { page: "products_pricing", path: "pricing" },
  { page: "financial_metrics", path: "financial-metrics" },
];

export function CrmLandingRedirect() {
  const { canAccessPage } = usePermissions();
  const first = LANDING_ORDER.find((entry) => canAccessPage(entry.page));
  // Profile is reachable by any authenticated CRM user, so it's the safe
  // last-resort landing for a session that somehow has no page grants.
  return <Navigate to={first ? first.path : "profile"} replace />;
}

export function RequireFinancialOutlet() {
  return (
    <PagePermissionGuard requiredPermission="financials" redirectTo="/crm">
      <Outlet />
    </PagePermissionGuard>
  );
}
